import { type Collection } from '../api/client'
import { useAuth } from '../store/auth'
import { useSettings } from '../store/settings'
import { usePullAll, PULL_COLLECTIONS } from '../store/pullAll'
import { formatDuration } from '../utils/time'

const LABEL: Record<Collection, string> = {
  'app.bsky.feed.post': 'posts',
  'app.bsky.feed.repost': 'reposts',
  'app.bsky.feed.like': 'likes',
  'app.bsky.graph.follow': 'follows',
  'app.bsky.graph.block': 'blocks',
  'app.bsky.graph.list': 'lists',
  'app.bsky.graph.starterpack': 'packs',
  'app.bsky.feed.generator': 'feeds',
}

export function PullOverlay() {
  const state = usePullAll((s) => s.state)
  const visible = usePullAll((s) => s.visible)
  const cancel = usePullAll((s) => s.cancel)
  const cancelCollection = usePullAll((s) => s.cancelCollection)
  const resumeCollection = usePullAll((s) => s.resumeCollection)
  const hide = usePullAll((s) => s.hide)
  const reset = usePullAll((s) => s.reset)
  const service = useSettings((s) => s.service)
  const accessJwt = useAuth((s) => s.session?.accessJwt)

  if (!visible) return null

  const totalFetched = PULL_COLLECTIONS.reduce(
    (n, c) => n + state.byCollection[c].fetched,
    0,
  )
  const doneCount = PULL_COLLECTIONS.reduce(
    (n, c) => n + (state.byCollection[c].done ? 1 : 0),
    0,
  )
  const overallPct = Math.round((doneCount / PULL_COLLECTIONS.length) * 100)
  const elapsed = state.startedAt
    ? (state.finishedAt ?? performance.now()) - state.startedAt
    : 0

  // True if every collection is in a settled state (done OR paused OR error).
  // Drives the title text — "running / paused / complete".
  const anyRunning = state.running
  const allDone = doneCount === PULL_COLLECTIONS.length

  return (
    <div className="fixed inset-0 z-[80] grid place-items-center bg-black/60 p-4 backdrop-blur-md">
      <div
        className="w-full max-w-xl rounded-3xl border p-5 shadow-glass backdrop-blur-xl"
        style={{
          background: 'rgba(8, 14, 32, 0.92)',
          borderColor: 'rgba(56, 189, 248, 0.3)',
        }}
      >
        <div className="mb-2 flex items-baseline justify-between">
          <h2 className="text-lg font-extrabold tracking-tight">
            {anyRunning
              ? `Pulling everything for @${state.handle ?? '…'}`
              : allDone
                ? 'Pull complete'
                : 'Pull paused'}
          </h2>
          <span className="text-xs text-ink-300">
            {totalFetched.toLocaleString()} records • {formatDuration(elapsed)}
          </span>
        </div>

        <div className="mb-4 h-2.5 w-full overflow-hidden rounded-full bg-white/5">
          <div
            className="h-2.5 rounded-full transition-all"
            style={{ width: `${overallPct}%`, background: 'var(--accent-strong)' }}
          />
        </div>
        <div className="mb-4 text-xs text-ink-300">
          {doneCount} / {PULL_COLLECTIONS.length} collections done ({overallPct}%)
        </div>

        {state.hydration.running || state.hydration.processed > 0 ? (
          <div className="mb-4 rounded-2xl border border-white/10 bg-white/5 px-3 py-2 text-xs">
            <div className="flex items-baseline justify-between">
              <strong className="text-white">
                {state.hydration.running ? 'Tagging media + sensitivity labels…' : 'Tagging complete'}
              </strong>
              <span className="text-ink-300">
                {state.hydration.processed.toLocaleString()}
                {state.hydration.total > 0 ? ` / ${state.hydration.total.toLocaleString()}` : ''}
              </span>
            </div>
            <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-white/5">
              <div
                className="h-1.5 rounded-full transition-all"
                style={{
                  width: `${
                    state.hydration.total > 0
                      ? Math.min(
                          100,
                          Math.round((state.hydration.processed / state.hydration.total) * 100),
                        )
                      : state.hydration.running
                        ? 5
                        : 100
                  }%`,
                  background: 'var(--accent-strong)',
                }}
              />
            </div>
          </div>
        ) : null}

        <div className="grid gap-2 text-xs sm:grid-cols-2">
          {PULL_COLLECTIONS.map((c) => {
            const p = state.byCollection[c]
            const isInflight = !p.done && !p.paused && !p.error && state.startedAt !== null
            const status = p.error
              ? 'error'
              : p.done
                ? 'done'
                : p.paused
                  ? 'paused'
                  : isInflight
                    ? 'running'
                    : 'idle'
            const dotClass =
              status === 'error'
                ? 'bg-rose-500/30 text-rose-100'
                : status === 'done'
                  ? 'bg-emerald-500/30 text-emerald-100'
                  : status === 'paused'
                    ? 'bg-amber-500/30 text-amber-100'
                    : status === 'running'
                      ? 'bg-sky-500/30 text-sky-100'
                      : 'bg-white/10 text-ink-100'
            const dotChar =
              status === 'error'
                ? '!'
                : status === 'done'
                  ? '✓'
                  : status === 'paused'
                    ? '⏸'
                    : '…'
            return (
              <div
                key={c}
                className="flex items-center gap-2 rounded-xl border border-white/10 bg-black/30 px-3 py-2"
              >
                <span
                  className={`grid h-5 w-5 flex-shrink-0 place-items-center rounded-full text-[10px] ${dotClass}`}
                  title={status}
                >
                  {dotChar}
                </span>
                <span className="flex-1 font-semibold">{LABEL[c]}</span>
                <span className="font-mono text-[10px] text-ink-300">
                  {p.fetched.toLocaleString()} • {p.pages}p
                </span>
                {status === 'running' ? (
                  <button
                    type="button"
                    onClick={() => cancelCollection(c)}
                    title={`pause ${LABEL[c]} (cursor preserved — resume picks up here)`}
                    className="grid h-5 w-5 place-items-center rounded-full border border-white/15 bg-white/5 text-[10px] text-ink-100 hover:border-amber-300 hover:bg-amber-500/30 hover:text-white"
                    aria-label={`pause ${LABEL[c]}`}
                  >
                    ⏸
                  </button>
                ) : status === 'paused' || (status === 'error' && p.cursor) ? (
                  <button
                    type="button"
                    onClick={() => {
                      if (state.handle) resumeCollection(c, service, accessJwt)
                    }}
                    title={`resume ${LABEL[c]} from saved cursor`}
                    className="grid h-5 w-5 place-items-center rounded-full border text-[10px] text-white"
                    style={{
                      borderColor: 'var(--accent-strong)',
                      background: 'var(--accent-soft)',
                    }}
                    aria-label={`resume ${LABEL[c]}`}
                  >
                    ▶
                  </button>
                ) : null}
              </div>
            )
          })}
        </div>

        <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
          {anyRunning ? (
            <button
              type="button"
              className="glass-button text-xs"
              onClick={cancel}
              title="pause every running section + hydration"
            >
              cancel all
            </button>
          ) : null}
          <button
            type="button"
            className="glass-button text-xs"
            onClick={() => {
              hide()
              if (!anyRunning) reset()
            }}
            title={anyRunning ? 'hide overlay (pull keeps running in the background)' : 'close and clear pull state'}
          >
            {anyRunning ? 'hide' : 'close'}
          </button>
        </div>
      </div>
    </div>
  )
}
